import { useSuspenseQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { useEnableGcpFakeSystemEngine } from "featureFlags/hooks/useEnableGcpFakeSystemEngine";
import { systemEngineEnvironment } from "services/environment/systemEngine";
import { ReactQueryKeysAccount } from "services/queryKeys";

import { useCurrentAccount } from "components/Account/useCurrentAccount";

import { SYSTEM_DATABASE_NAMES } from "./utils";

export type Database = {
  catalogName: string;
  description: string;
  catalogOwner: string;
  created: string;
  lastAltered: string;
};

interface Options {
  isFirstUser?: boolean;
  showSystemDatabases?: boolean;
  accountName?: string;
  endpoint?: string;
}

export const fetchDatabasesNames = async (options?: Options) => {
  const result = await systemEngineEnvironment.execute<Database>(
    `SELECT catalog_name, description, catalog_owner, created, last_altered FROM information_schema.catalogs order by LOWER(catalog_name)`,
    { accountName: options?.accountName, endpoint: options?.endpoint }
  );

  const [response] = result;
  return response.rows;
};

export const useDatabasesNames = (options?: Options): Database[] => {
  const { getAccount } = useCurrentAccount();
  const account = getAccount();
  const [dataLength, setDataLength] = useState(0);
  const isFirstUser = options?.isFirstUser;
  const endpoint = useEnableGcpFakeSystemEngine();

  const queryKey: string[] = [
    ReactQueryKeysAccount.databasesNames,
    options?.accountName ?? account.accountName,
  ];

  const { data } = useSuspenseQuery<Database[], Error>({
    queryKey,
    queryFn: () => fetchDatabasesNames({ ...options, endpoint }),
    refetchInterval: isFirstUser && !dataLength ? 2000 : false,
  });

  useEffect(() => {
    setDataLength(data.length);
  }, [data.length]);

  const databasesNames = useMemo(() => {
    return options?.showSystemDatabases
      ? data
      : data.filter(db => !SYSTEM_DATABASE_NAMES.includes(db.catalogName));
  }, [options?.showSystemDatabases, data]);

  return databasesNames || [];
};
